import type {
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from 'axios'
import axios from 'axios'
import {
  METHODS,
  COMMON_KEYS,
  STATUS_CODES,
  IGNORED_REFRESH_ROUTES,
} from '../config/constants'
import { router } from 'src/router'
import { useAuthStore } from 'src/common/store/auth.store'
import { Roles } from 'src/common/types'

export const axiosInstance = axios.create({
  baseURL: process.env.BASE_API,
})

interface IRequestParams {
  url: string
  method: string
  headers?: Record<string, unknown>
  params?: Record<string, string | number>
  data?: unknown
}

export const request = async <T>({
  method = METHODS.get,
  headers = {},
  ...res
}: IRequestParams): Promise<AxiosResponse<T>> => {
  const requestHeaders = Object.assign(headers, {
    [COMMON_KEYS.contentType]:
      headers[COMMON_KEYS.contentType] ?? COMMON_KEYS.appJson,
  })

  const request: AxiosRequestConfig = {
    method: method as Method,
    headers: requestHeaders as AxiosHeaders,
    ...res,
  }

  return axiosInstance(request)
}

axiosInstance.interceptors.request.use(async (config) => {
  let sk: string | undefined = ''

  if (config.url) {
    sk = process.env.SK_MASTER
  }

  const headers = config.headers || {}
  const authStore = useAuthStore()
  const tryRefresh = !IGNORED_REFRESH_ROUTES.some((route) =>
    config.url?.includes(route),
  )
  const token = (await authStore.getAccessToken(tryRefresh)) || ''

  Object.assign(config.headers || {}, {
    ...headers,
    centerId:
      (headers as Record<string, string>).centerId ||
      authStore.currentCenterId ||
      '',
    orgId:
      (headers as Record<string, string>).orgId || authStore.currentOrgId || '',
    auth: token || '',
    client: authStore.tokenPayload?.extension_Role === Roles.Client || false,
    sk,
  })

  return config
})

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const errorResponse = error.response
    const originalRequest = error.config

    // Throw error if issue is no response. I.e. no internet connection
    if (!errorResponse && error.code && error.code === 'ERR_NETWORK') {
      throw error
    }

    if (errorResponse?.status === STATUS_CODES.unauthorized) {
      if (
        !originalRequest._retry &&
        !IGNORED_REFRESH_ROUTES.some((route) =>
          originalRequest.url.includes(route),
        )
      ) {
        originalRequest._retry = true
        try {
          // Try a second time, pre-request interceptor will attempt token refresh if necessary
          const data = axiosInstance(originalRequest)
          return data
        } catch {
          // Fail Silently
        }
      }
      originalRequest._retry = false
      const currentRoute = router.currentRoute.value
      if (
        currentRoute.name !== 'admin.login' &&
        currentRoute.name !== 'client.login' &&
        currentRoute.name !== 'client.centerSelection'
      ) {
        const authStore = useAuthStore()
        const routeName = authStore.isClient ? 'client.login' : 'admin.login'
        await authStore.logout()
        void router.push({
          name: routeName,
        })
      }
    }

    if (errorResponse?.status === STATUS_CODES.gone) {
      void router.replace({ name: 'outdatedVersion' })
    }

    return Promise.reject(error)
  },
)
